import jsPDF from "jspdf";
import { sanitizeFileName } from "utils/sanitizeFileName";
import type { ExportReportToPdfArgs, ReportPdfLabels } from "./types";
import { FRONTEND_URL } from "config/config";

const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 20;
const CONTENT_W = PAGE_W - MARGIN * 2;
const FONT_SIZE = {
  title: 20,
  sectionTitle: 13,
  subtitle: 10,
  body: 9,
  small: 8,
  footer: 7,
};
const LINE_HEIGHT = 4.5;
const SECTION_GAP = 10;

const COLORS = {
  primary: [17, 24, 39] as [number, number, number], // gray-900
  secondary: [107, 114, 128] as [number, number, number], // gray-500
  accent: [37, 99, 235] as [number, number, number], // blue-600
  light: [229, 231, 235] as [number, number, number], // gray-200
  white: [255, 255, 255] as [number, number, number],
  red: [220, 38, 38] as [number, number, number],
  amber: [217, 119, 6] as [number, number, number],
  green: [22, 163, 74] as [number, number, number],
  bgLight: [249, 250, 251] as [number, number, number], // gray-50
};

export const exportReportToPdf = async ({
  report,
  labels,
}: ExportReportToPdfArgs): Promise<void> => {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  let y = MARGIN;

  const ensureSpace = (needed: number) => {
    if (y + needed > PAGE_H - MARGIN - 16) {
      doc.addPage();
      y = MARGIN;
    }
  };

  const drawSectionHeader = (
    title: string,
    color: [number, number, number] = COLORS.primary,
  ) => {
    ensureSpace(16);
    y += SECTION_GAP;
    doc.setFillColor(...color);
    doc.roundedRect(MARGIN, y, 4, 8, 1, 1, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(FONT_SIZE.sectionTitle);
    doc.setTextColor(...COLORS.primary);
    doc.text(title, MARGIN + 7, y + 6);
    y += 14;
  };

  const drawKeyValue = (label: string, value: string) => {
    ensureSpace(8);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(FONT_SIZE.small);
    doc.setTextColor(...COLORS.secondary);
    doc.text(label.toUpperCase(), MARGIN, y);
    y += LINE_HEIGHT;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(FONT_SIZE.body);
    doc.setTextColor(...COLORS.primary);
    const lines = doc.splitTextToSize(value, CONTENT_W);
    for (const line of lines) {
      ensureSpace(LINE_HEIGHT);
      doc.text(line, MARGIN, y);
      y += LINE_HEIGHT;
    }
    y += 2;
  };

  const drawBulletList = (label: string, items: string[]) => {
    ensureSpace(8);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(FONT_SIZE.small);
    doc.setTextColor(...COLORS.secondary);
    doc.text(label.toUpperCase(), MARGIN, y);
    y += LINE_HEIGHT + 1;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(FONT_SIZE.body);
    doc.setTextColor(...COLORS.primary);
    for (const item of items) {
      const lines = doc.splitTextToSize(item, CONTENT_W - 6);
      ensureSpace(lines.length * LINE_HEIGHT + 1);
      doc.setFillColor(...COLORS.secondary);
      doc.circle(MARGIN + 1.5, y - 1.2, 0.8, "F");
      for (let j = 0; j < lines.length; j++) {
        doc.text(lines[j], MARGIN + 5, y);
        y += LINE_HEIGHT;
      }
    }
    y += 2;
  };

  // ═══════════════════════════════════════════════════════════
  // COVER / HEADER
  // ═══════════════════════════════════════════════════════════
  try {
    const logoImg = await loadImage("/monco-logo-black.png");
    // Preserve aspect ratio: measure natural size, fit to max width 36mm
    const maxW = 36;
    const ratio = logoImg.naturalHeight / logoImg.naturalWidth;
    const logoW = maxW;
    const logoH = maxW * ratio;
    doc.addImage(logoImg, "PNG", MARGIN, y, logoW, logoH);
    // Make logo area clickable
    doc.link(MARGIN, y, logoW, logoH, { url: FRONTEND_URL });
    y += logoH + 4;
  } catch {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(...COLORS.accent);
    doc.textWithLink("Monco.ai", MARGIN, y + 6, { url: FRONTEND_URL });
    y += 14;
  }

  // Thin separator
  doc.setDrawColor(...COLORS.light);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y);
  y += 8;

  // Report title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(FONT_SIZE.title);
  doc.setTextColor(...COLORS.primary);
  doc.text(labels.title, MARGIN, y);
  y += 8;

  // Project name
  if (report.project_name) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(FONT_SIZE.subtitle);
    doc.setTextColor(...COLORS.secondary);
    doc.text(report.project_name, MARGIN, y);
    y += 6;
  }

  // Separator
  doc.setDrawColor(...COLORS.light);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y);
  y += 4;

  // ═══════════════════════════════════════════════════════════
  // PROJECT OVERVIEW
  // ═══════════════════════════════════════════════════════════
  drawSectionHeader(labels.projectOverview);

  // Stats row
  const statsRow: { label: string; value: string }[] = [];
  if (report.project_type)
    statsRow.push({ label: "Type", value: report.project_type });
  if (report.location_info)
    statsRow.push({ label: "Location", value: report.location_info });
  if (report.complexity_rating)
    statsRow.push({
      label: labels.complexity,
      value: report.complexity_rating,
    });
  if (
    report.document_completeness !== undefined &&
    report.document_completeness !== null
  ) {
    statsRow.push({
      label: labels.completeness,
      value: `${Math.round(report.document_completeness * 100)}%`,
    });
  }
  if (report.estimated_total_circuits)
    statsRow.push({
      label: labels.circuits,
      value: `~${report.estimated_total_circuits}`,
    });
  if (report.boq_total_positions)
    statsRow.push({
      label: labels.boqPositions,
      value: `${report.boq_total_positions}`,
    });

  if (statsRow.length > 0) {
    ensureSpace(14);
    const colW = CONTENT_W / Math.min(statsRow.length, 4);
    doc.setFillColor(...COLORS.bgLight);
    doc.roundedRect(MARGIN, y - 2, CONTENT_W, 12, 2, 2, "F");

    statsRow.slice(0, 4).forEach((stat, i) => {
      const x = MARGIN + i * colW + 4;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(FONT_SIZE.small);
      doc.setTextColor(...COLORS.secondary);
      doc.text(stat.label, x, y + 2);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(FONT_SIZE.body);
      doc.setTextColor(...COLORS.primary);
      doc.text(stat.value, x, y + 7);
    });
    y += 14;
  }

  if (report.electrical_scope)
    drawKeyValue(labels.scope, report.electrical_scope);
  if (report.applicable_standards?.length)
    drawBulletList(labels.standards, report.applicable_standards);
  if (report.manufacturers?.length)
    drawBulletList(labels.manufacturers, report.manufacturers);

  if (report.distribution_boards?.length) {
    drawKeyValue(
      labels.distributionBoards,
      report.distribution_boards
        .map((b) => {
          let line = b.name;
          if (b.location) line += ` — ${b.location}`;
          if (b.estimated_circuits)
            line += ` (~${b.estimated_circuits} circuits)`;
          return line;
        })
        .join("\n"),
    );
  }

  // ═══════════════════════════════════════════════════════════
  // SURVEYOR ALERTS
  // ═══════════════════════════════════════════════════════════
  if (report.surveyor_alerts?.length) {
    drawSectionHeader(labels.surveyorAlerts, COLORS.red);

    for (const alert of report.surveyor_alerts) {
      ensureSpace(10);
      const severityColor =
        alert.severity === "critical"
          ? COLORS.red
          : alert.severity === "warning"
            ? COLORS.amber
            : COLORS.accent;

      doc.setFillColor(...severityColor);
      doc.circle(MARGIN + 2, y - 1, 1.2, "F");

      const severityLabel =
        labels.severityLabels[alert.severity] || alert.severity;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(FONT_SIZE.small);
      doc.setTextColor(...severityColor);
      doc.text(severityLabel.toUpperCase(), MARGIN + 6, y);

      y += LINE_HEIGHT;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(FONT_SIZE.body);
      doc.setTextColor(...COLORS.primary);
      const lines = doc.splitTextToSize(alert.message, CONTENT_W - 6);
      for (const line of lines) {
        ensureSpace(LINE_HEIGHT);
        doc.text(line, MARGIN + 6, y);
        y += LINE_HEIGHT;
      }
      y += 2;
    }
  }

  // ═══════════════════════════════════════════════════════════
  // SCOPE SUMMARY
  // ═══════════════════════════════════════════════════════════
  if (report.scope_summary) {
    drawSectionHeader(labels.scopeSummary);
    const summary = report.scope_summary;

    const textFields: { key: string; value?: string }[] = [
      { key: "summary", value: summary.summary },
      { key: "scopeOfWork", value: summary.scope_of_work },
      { key: "warrantyAndLiability", value: summary.warranty_and_liability },
      { key: "timelineAndMilestones", value: summary.timeline_and_milestones },
    ];

    for (const f of textFields) {
      if (f.value && labels.scopeFields[f.key]) {
        drawKeyValue(labels.scopeFields[f.key], f.value);
      }
    }

    const listFields: { key: string; items?: string[] }[] = [
      { key: "specialConditions", items: summary.special_conditions },
      { key: "materialRequirements", items: summary.material_requirements },
      {
        key: "standardsAndRegulations",
        items: summary.standards_and_regulations,
      },
      { key: "keyQuantities", items: summary.key_quantities },
    ];

    for (const f of listFields) {
      if (f.items?.length && labels.scopeFields[f.key]) {
        drawBulletList(labels.scopeFields[f.key], f.items);
      }
    }

    if (summary.surveyor_notes?.length) {
      drawBulletList(
        labels.scopeFields["surveyorNotes"] || "Surveyor Notes",
        summary.surveyor_notes,
      );
    }
  }

  // ═══════════════════════════════════════════════════════════
  // BOQ SECTIONS
  // ═══════════════════════════════════════════════════════════
  if (report.boq_sections?.length) {
    drawSectionHeader(labels.boqSections);

    for (const section of report.boq_sections) {
      ensureSpace(12);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(FONT_SIZE.body);
      doc.setTextColor(...COLORS.primary);
      doc.text(
        `${section.section_number}  ${section.section_title}`,
        MARGIN,
        y,
      );

      const meta = `${section.position_count} ${labels.positions}`;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(FONT_SIZE.small);
      doc.setTextColor(...COLORS.secondary);
      doc.text(meta, PAGE_W - MARGIN, y, { align: "right" });
      y += LINE_HEIGHT + 1;

      if (section.key_items?.length) {
        for (const item of section.key_items.slice(0, 5)) {
          ensureSpace(LINE_HEIGHT);
          doc.setFontSize(FONT_SIZE.small);
          doc.setTextColor(...COLORS.secondary);
          const itemText = `${item.position_number}  ${item.description}${item.quantity ? ` — ${item.quantity} ${item.unit || ""}` : ""}`;
          const lines = doc.splitTextToSize(itemText, CONTENT_W - 6);
          for (const line of lines) {
            ensureSpace(LINE_HEIGHT);
            doc.text(line, MARGIN + 4, y);
            y += LINE_HEIGHT;
          }
        }
      }

      if (section.warnings?.length) {
        for (const warning of section.warnings) {
          ensureSpace(LINE_HEIGHT);
          doc.setTextColor(...COLORS.amber);
          doc.setFontSize(FONT_SIZE.small);
          const lines = doc.splitTextToSize(`! ${warning}`, CONTENT_W - 6);
          for (const line of lines) {
            ensureSpace(LINE_HEIGHT);
            doc.text(line, MARGIN + 4, y);
            y += LINE_HEIGHT;
          }
        }
      }

      y += 3;
    }
  }

  // ═══════════════════════════════════════════════════════════
  // FILE INVENTORY
  // ═══════════════════════════════════════════════════════════
  if (report.file_names?.length) {
    drawSectionHeader(labels.fileInventory);

    // Table header
    ensureSpace(10);
    const colWidths = [55, 45, 15, 30, 25];
    const headers = [
      labels.fileHeaders.suggested,
      labels.fileHeaders.original,
      labels.fileHeaders.pages,
      labels.fileHeaders.types,
      labels.fileHeaders.focus,
    ];

    doc.setFillColor(...COLORS.bgLight);
    doc.rect(MARGIN, y - 3, CONTENT_W, 7, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(FONT_SIZE.small);
    doc.setTextColor(...COLORS.secondary);
    let xPos = MARGIN + 2;
    headers.forEach((h, i) => {
      doc.text(h, xPos, y);
      xPos += colWidths[i];
    });
    y += 6;

    for (const file of report.file_names) {
      ensureSpace(8);
      xPos = MARGIN + 2;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(FONT_SIZE.small);
      doc.setTextColor(...COLORS.primary);
      doc.text((file.suggested || file.original).substring(0, 28), xPos, y);
      xPos += colWidths[0];
      doc.setTextColor(...COLORS.secondary);
      doc.text(file.original.substring(0, 22), xPos, y);
      xPos += colWidths[1];
      doc.text(String(file.page_count || "—"), xPos, y);
      xPos += colWidths[2];
      doc.text((file.doc_types?.join(", ") || "").substring(0, 16), xPos, y);
      xPos += colWidths[3];
      doc.text((file.surveyor_focus || "").substring(0, 14), xPos, y);
      y += LINE_HEIGHT + 1;
    }
  }

  // ═══════════════════════════════════════════════════════════
  // ZONES
  // ═══════════════════════════════════════════════════════════
  if (report.zones?.length) {
    drawSectionHeader(labels.zoneStructure);

    for (const zone of report.zones) {
      ensureSpace(14);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(FONT_SIZE.body);
      doc.setTextColor(...COLORS.primary);
      doc.text(`${zone.zone_id}  ${zone.name}`, MARGIN, y);
      if (zone.scope) {
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...COLORS.accent);
        doc.text(zone.scope, PAGE_W - MARGIN, y, { align: "right" });
      }
      y += LINE_HEIGHT + 1;

      if (zone.description) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(FONT_SIZE.small);
        doc.setTextColor(...COLORS.secondary);
        const lines = doc.splitTextToSize(zone.description, CONTENT_W);
        for (const line of lines) {
          ensureSpace(LINE_HEIGHT);
          doc.text(line, MARGIN, y);
          y += LINE_HEIGHT;
        }
      }

      const meta: string[] = [];
      if (zone.page_count) meta.push(`${zone.page_count} pages`);
      if (zone.floor_levels?.length)
        meta.push(`Floors: ${zone.floor_levels.join(", ")}`);
      if (zone.distribution_boards?.length)
        meta.push(`Boards: ${zone.distribution_boards.join(", ")}`);
      if (meta.length > 0) {
        ensureSpace(LINE_HEIGHT);
        doc.setFontSize(FONT_SIZE.small);
        doc.setTextColor(...COLORS.secondary);
        doc.text(meta.join("  |  "), MARGIN, y);
        y += LINE_HEIGHT;
      }
      y += 3;
    }
  }

  // Final footer
  addFooter(doc, labels);

  // Save
  const baseName = sanitizeFileName({
    rawName: report.project_name || "Research-Report",
    fallback: "Research-Report",
  });
  const filename = `${baseName}_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
};

const addFooter = (doc: jsPDF, labels: ReportPdfLabels) => {
  const pageCount = doc.getNumberOfPages();

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);

    // Separator line
    doc.setDrawColor(...COLORS.light);
    doc.setLineWidth(0.2);
    doc.line(MARGIN, PAGE_H - 14, PAGE_W - MARGIN, PAGE_H - 14);

    // Left: brand + link
    doc.setFont("helvetica", "normal");
    doc.setFontSize(FONT_SIZE.footer);
    doc.setTextColor(...COLORS.secondary);
    doc.text(`${labels.generatedBy} Monco.ai`, MARGIN, PAGE_H - 10);
    doc.setTextColor(...COLORS.accent);
    doc.textWithLink(FRONTEND_URL, MARGIN, PAGE_H - 7, { url: FRONTEND_URL });

    // Right: page number
    doc.setTextColor(...COLORS.secondary);
    doc.text(
      `${labels.page} ${i} / ${pageCount}`,
      PAGE_W - MARGIN,
      PAGE_H - 10,
      { align: "right" },
    );
  }
};

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
